<template>
    <div class="GroupRulesTable">
        <div class="GroupRulesTable__row GroupRulesTable__row--header">
            <span>Group object ID</span>
            <span>Role</span>
            <span />
        </div>
        <p v-if="rules.length === 0" class="GroupRulesTable__empty">
            No groups are mapped yet.
        </p>
        <div v-for="(rule, index) in rules" :key="index" class="GroupRulesTable__row">
            <!-- Each control is wrapped so the tooltip has something to hang on: a disabled element
                 fires no mouse events, so a directive placed on it would never show. -->
            <span v-tooltip="reasonFor(rule)" class="GroupRulesTable__cell">
                <TextInput
                    :id="`ssoGroupId${index}`"
                    :data-test="`group-id-${index}`"
                    :value="rule.groupObjectId"
                    :error="rowErrors[index] || ''"
                    :disabled="disabled || isProtected(rule)"
                    @input="update(index, { groupObjectId: $event.target.value })"
                />
            </span>
            <span v-tooltip="reasonFor(rule)" class="GroupRulesTable__cell">
                <EnumInput
                    :id="`ssoGroupRole${index}`"
                    :data-test="`group-role-${index}`"
                    :value="rule.role"
                    :options="roleOptions"
                    :disabled="disabled || isProtected(rule)"
                    @input="update(index, { role: $event })"
                />
            </span>
            <span v-tooltip="reasonFor(rule)">
                <NewButton
                    :data-test="`remove-${index}`"
                    variant="outline"
                    color="danger"
                    :disabled="disabled || isProtected(rule)"
                    @click="remove(index)"
                >
                    Remove
                </NewButton>
            </span>
        </div>
        <div>
            <NewButton
                data-test="add-rule"
                variant="outline"
                color="primary"
                :disabled="disabled"
                @click="add"
            >
                Add group mapping
            </NewButton>
        </div>
    </div>
</template>

<style lang="scss">
    .GroupRulesTable {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3, 12px);

        &__row {
            display: grid;
            // Sized to the content rather than the viewport: a group object ID is a GUID or a short
            // name, so a 1fr first column left a very wide box with a handful of characters in it.
            // `justify-content: start` keeps the row from stretching to fill the page.
            grid-template-columns: minmax(200px, 340px) 200px auto;
            justify-content: start;
            gap: var(--spacing-3, 12px);
            align-items: start;

            @media (max-width: 720px) {
                grid-template-columns: 1fr;
                justify-content: stretch;
            }

            &--header {
                font-size: var(--text-body-size);
                font-weight: 600;
                color: var(--eidDarkBlue);

                // Stacked, the three header labels would each become their own row above the
                // first input and read as content rather than as column headings.
                @media (max-width: 720px) {
                    display: none;
                }
            }
        }

        // The tooltip wrappers are grid items, so they must fill their column and be allowed to
        // shrink -- without `min-width: 0` a long group ID forces the column wider than its track.
        &__cell {
            display: block;
            min-width: 0;
        }

        &__empty {
            margin: 0;
            font-size: var(--text-body-size);
            color: var(--eidTechGray);
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { SsoGroupRuleView } from '@evidentid/tprm-portal-lib/models/sso-provisioning';

    import NewButton from '@/components/new-button/NewButton.vue';
    import EnumInput from '@/modules/dynamic-form/components/style-inputs/enum-input/EnumInput.vue';
    import TextInput from '@/modules/dynamic-form/components/style-inputs/text-input/TextInput.vue';
    import { ROLE_OPTIONS } from '@/modules/sso-provisioning/utils/roles';

    export default defineComponent({
        name: 'GroupRulesTable',
        components: { EnumInput, NewButton, TextInput },
        props: {
            rules: { type: Array as PropType<SsoGroupRuleView[]>, required: true },
            rowErrors: { type: Array as PropType<(string | null)[]>, default: () => [] },
            // Rows that must not be removed: the rule the signed-in admin's own Admin hangs on.
            // Worked out by the view from their IdP groups, not sent by the API -- see
            // utils/selfLockout. Empty whenever that cannot be judged, so the table stays usable.
            protectedGroupIds: { type: Array as PropType<string[]>, default: () => [] },
            disabled: { type: Boolean, default: false },
        },
        emits: [ 'update:rules' ],
        data() {
            return {
                roleOptions: ROLE_OPTIONS,
                // Says the way out, because the whole row is read-only: there is no one-step edit
                // that keeps you an Admin, so the only safe route is to add the replacement first.
                protectedReason: 'This is the rule that grants you Admin access. '
                    + 'Map another group you belong to to Admin before changing this one.',
            };
        },
        methods: {
            isProtected(rule: SsoGroupRuleView): boolean {
                return this.protectedGroupIds.includes(rule.groupObjectId);
            },
            reasonFor(rule: SsoGroupRuleView): string {
                return this.isProtected(rule) ? this.protectedReason : '';
            },
            update(index: number, patch: Partial<SsoGroupRuleView>): void {
                this.$emit('update:rules', this.rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
            },
            remove(index: number): void {
                this.$emit('update:rules', this.rules.filter((_, i) => i !== index));
            },
            add(): void {
                this.$emit('update:rules', [ ...this.rules, { groupObjectId: '', role: 'viewer' } ]);
            },
        },
    });
</script>
